import { batch, signal, computed } from '@preact/signals'
import type { Row, SessionsOp, SessionsSnapshot } from '../../../protocol/views.ts'
import { keyedStore } from '../store/keyed.ts'
import { groupHeadings, type Group } from '../../../client/status.ts'
import { dayOf } from '../../../client/format.ts'
import type { StoreOp } from '../store/keyed.ts'

export type Mode = 'workspace' | 'history'
export type RowType = 'one-line' | 'two-line' | 'result'
export type DisplayEntry = { kind: 'group'; id: string; label: string } | { kind: 'row'; ref: string; group: string; type: RowType }
export interface DisplayRow { row: Row; removed: boolean }
export type Hold = 'touch' | 'pointer' | 'scroll' | 'away' | 'menu' | 'focus'
type Index = { group: string; time: number; type: RowType }
export type SourceGroup = Group | 'history'
type Partition = Pick<SessionsSnapshot, 'total' | 'next'> & { group: SourceGroup; root: boolean }
export interface DisplaySource { source: string; group: SourceGroup; root?: boolean; data: SessionsSnapshot }
const samePartition = (a: Partition | undefined, b: Partition | undefined) => a === b || !!a && !!b && a.total === b.total && a.group === b.group && a.root === b.root && a.next?.ref === b.next?.ref && a.next?.orderAt === b.next?.orderAt
export interface DisplayState {
  mode: Mode
  lastInput: boolean
  index: ReadonlyMap<string, Index>
  sources: ReadonlyMap<string, ReadonlySet<string>>
  partitions: ReadonlyMap<string, Partition>
  shown: readonly DisplayEntry[]
  target: readonly DisplayEntry[]
  holds: ReadonlySet<Hold>
  guardUntil: number
  moved: ReadonlyMap<string, number>
}
export type DisplayEvent =
  | ({ type: 'snapshot'; now?: number } & DisplaySource)
  | { type: 'replace'; sources: readonly DisplaySource[]; now?: number }
  | { type: 'patch'; source: string; ops: readonly SessionsOp[]; now?: number }
  | { type: 'reset'; source: string; now?: number }
  | { type: 'drop'; source: string; now?: number }
  | { type: 'hold'; reason: Hold; on: boolean; now: number }
  | { type: 'release'; now: number }
  | { type: 'tick'; now: number }
  | { type: 'mode'; mode: Mode; now: number }
type Write = { ref: string; row?: Row; removed?: boolean }

export function initialDisplay(mode: Mode = 'workspace', lastInput = false): DisplayState {
  return { mode, lastInput, index: new Map(), sources: new Map(), partitions: new Map(), shown: [], target: [], holds: new Set(), guardUntil: 0, moved: new Map() }
}
function day(time: number) {
  const date = new Date(time)
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}
function structural(row: Row, state: DisplayState, group: string): Index {
  const time = row.orderAt
  return { group: state.mode === 'history' ? day(time) : group, time, type: 'two-line' }
}
function entries(index: ReadonlyMap<string, Index>, mode: Mode, now: number): DisplayEntry[] {
  const groups = new Map<string, { ref: string; value: Index }[]>()
  for (const [ref, value] of index) {
    const rows = groups.get(value.group) ?? []
    rows.push({ ref, value }); groups.set(value.group, rows)
  }
  const names = mode === 'workspace' ? groupHeadings.map(({ group }) => group).filter(g => groups.has(g)) : [...groups.keys()].sort().reverse()
  const labels = new Map<string, string>(groupHeadings.map(({ group, heading }) => [group, heading]))
  return names.flatMap(group => {
    const rows = groups.get(group)!.sort((a, b) => b.value.time - a.value.time || (a.ref < b.ref ? -1 : a.ref > b.ref ? 1 : 0))
    const header: DisplayEntry[] = mode === 'history' || names.length > 1 ? [{ kind: 'group', id: group, label: labels.get(group) ?? dayOf(new Date(`${group}T12:00:00`).getTime(), now) }] : []
    return [...header, ...rows.map(({ ref, value }): DisplayEntry => ({ kind: 'row', ref, group, type: value.type }))]
  })
}
function sameEntries(a: readonly DisplayEntry[], b: readonly DisplayEntry[]) {
  return a === b || a.length === b.length && a.every((v, i) => v.kind === b[i].kind && (v.kind === 'row' ? b[i].kind === 'row' && v.ref === b[i].ref && v.group === b[i].group && v.type === b[i].type : b[i].kind === 'group' && v.id === b[i].id && v.label === b[i].label))
}
export function pendingCount(state: DisplayState) {
  if (state.shown === state.target) return 0
  const old = new Map(state.shown.flatMap((entry, i) => entry.kind === 'row' ? [[entry.ref, `${i}:${entry.group}:${entry.type}`] as const] : []))
  const changed = new Set<string>()
  state.target.forEach((entry, i) => {
    if (entry.kind !== 'row') return
    if (old.get(entry.ref) !== `${i}:${entry.group}:${entry.type}`) changed.add(entry.ref)
    old.delete(entry.ref)
  })
  old.forEach((_, ref) => changed.add(ref))
  return changed.size
}

/** Pure structural reducer. Row values are transient writes; only the keyed store retains their facts. */
export function reduceDisplay(previous: DisplayState, event: DisplayEvent, tapGuard = 180) {
  let state = previous
  let index = previous.index
  let sources = previous.sources
  let partitions = previous.partitions
  const writes = new Map<string, Write>()
  let dirty = false
  const now = event.now ?? 0
  const ownIndex = () => { if (index === previous.index) index = new Map(index); return index as Map<string, Index> }
  const ownSources = () => { if (sources === previous.sources) sources = new Map(sources); return sources as Map<string, ReadonlySet<string>> }
  const ownPartitions = () => { if (partitions === previous.partitions) partitions = new Map(partitions); return partitions as Map<string, Partition> }
  const putPartition = (source: string, value: Partition) => { if (!samePartition(partitions.get(source), value)) { ownPartitions().set(source, value) } }
  const groupFor = (ref: string) => groupHeadings.find(({ group }) => [...sources].some(([source, members]) => members.has(ref) && partitions.get(source)?.group === group))?.group ?? ''
  const regroup = (ref: string) => {
    const before = index.get(ref), group = groupFor(ref)
    if (before && state.mode === 'workspace' && before.group !== group) { ownIndex().set(ref, { ...before, group }); dirty = true }
  }
  const contains = (ref: string) => [...sources.values()].some(members => members.has(ref))
  const remove = (ref: string) => {
    if (contains(ref)) { regroup(ref); return }
    if (index.has(ref)) { ownIndex().delete(ref); dirty = true }
    writes.set(ref, { ref, removed: true })
  }
  const upsert = (row: Row) => {
    const next = structural(row, state, groupFor(row.ref)), current = index.get(row.ref)
    if (!current || current.group !== next.group || current.time !== next.time || current.type !== next.type) { ownIndex().set(row.ref, next); dirty = true }
    writes.set(row.ref, { ref: row.ref, row, removed: false })
  }
  if (event.type === 'snapshot' || event.type === 'replace') {
    const snapshots = event.type === 'snapshot' ? [event] : event.sources
    const previousMembers = new Set<string>()
    if (event.type === 'replace') {
      for (const members of sources.values()) for (const ref of members) previousMembers.add(ref)
      sources = new Map(); partitions = new Map(); dirty = true
    }
    for (const value of snapshots) {
      for (const ref of sources.get(value.source) ?? []) previousMembers.add(ref)
      ownSources().set(value.source, new Set(value.data.rows.map(row => row.ref)))
      putPartition(value.source, { total: value.data.total, next: value.data.next, group: value.group, root: value.root ?? true })
    }
    for (const ref of previousMembers) remove(ref)
    for (const value of snapshots) value.data.rows.forEach(upsert)
  } else if (event.type === 'patch' && partitions.has(event.source)) {
    let members = sources.get(event.source) ?? new Set<string>()
    let copied = false
    const changeMembers = () => {
      if (!copied) { copied = true; members = new Set(members); ownSources().set(event.source, members) }
      return members as Set<string>
    }
    for (const op of event.ops) {
      if (op.op === 'upsert') { if (!members.has(op.row.ref)) changeMembers().add(op.row.ref); upsert(op.row) }
      else if (op.op === 'remove') { if (members.has(op.ref)) changeMembers().delete(op.ref); remove(op.ref) }
      else {
        const old = partitions.get(event.source)!
        putPartition(event.source, op.op === 'total' ? { ...old, total: op.total } : { ...old, next: op.next })
      }
    }
  } else if (event.type === 'drop') {
    const before = sources.get(event.source)
    if (sources.has(event.source)) ownSources().delete(event.source)
    if (partitions.has(event.source)) ownPartitions().delete(event.source)
    before?.forEach(remove)
  } else if (event.type === 'hold') {
    const holds = new Set(state.holds)
    if (event.on) holds.add(event.reason)
    else holds.delete(event.reason)
    state = { ...state, holds, guardUntil: !event.on && state.holds.has(event.reason) && event.reason !== 'away' ? Math.max(state.guardUntil, now + tapGuard) : state.guardUntil }
  } else if (event.type === 'release') {
    const holds = new Set(state.holds); holds.delete('away')
    state = { ...state, holds, guardUntil: 0 }
  } else if (event.type === 'mode' && event.mode !== state.mode) {
    // Modes belong to separate page/query state. Reusing one model without a new snapshot would guess groups.
    state = { ...initialDisplay(event.mode, state.lastInput), holds: state.holds, guardUntil: state.guardUntil }
    index = state.index; sources = state.sources; partitions = state.partitions; dirty = true
    previous.index.forEach((_, ref) => writes.set(ref, { ref, removed: true }))
  }
  let target = dirty ? entries(index, state.mode, now) : state.target
  if (partitions !== previous.partitions && partitions.size === previous.partitions.size && [...partitions].every(([source, part]) => samePartition(part, previous.partitions.get(source)))) partitions = previous.partitions
  if (dirty) {
    if (sameEntries(target, state.target)) target = state.target
    if (sameEntries(target, state.shown)) target = state.shown
  }
  let shown = state.shown
  let moved = state.moved
  const committed = !state.holds.size && now >= state.guardUntil && shown !== target
  if (committed) {
    const positions = new Map(shown.flatMap((entry, i) => entry.kind === 'row' ? [[entry.ref, i] as const] : []))
    const guards = new Map([...moved].filter(([ref, until]) => index.has(ref) && until > now))
    target.forEach((entry, i) => { if (entry.kind === 'row' && positions.has(entry.ref) && positions.get(entry.ref) !== i) guards.set(entry.ref, now + tapGuard) })
    moved = guards
    shown = target
  }
  state = { ...state, index, sources, partitions, shown, target, moved }
  return { state, writes: [...writes.values()], committed }
}

export function createDisplay(options: { mode?: Mode; tapGuard?: number; lastInput?: boolean } = {}) {
  let state = initialDisplay(options.mode, options.lastInput)
  const rows = keyedStore<string, DisplayRow, null>(value => value.row.ref, null)
  const structure = signal<readonly DisplayEntry[]>(state.shown)
  const pending = signal(0)
  const deadline = signal(0)
  const partitions = signal<ReadonlyMap<string, Partition>>(state.partitions)
  return {
    rows, structure, pending, deadline, partitions,
    groupTotals: computed(() => Object.fromEntries(groupHeadings.map(({ group }) => { const roots = [...partitions.value.values()].filter(part => part.root && part.group === group); return [group, roots.length ? roots.reduce((sum, part) => sum + part.total, 0) : undefined] })) as Record<Group, number | undefined>),
    get state() { return state },
    dispatch(event: DisplayEvent) {
      const before = state
      const next = reduceDisplay(state, { ...event, now: event.now ?? Date.now() }, options.tapGuard)
      state = next.state
      batch(() => {
        const ops = next.writes.flatMap<StoreOp<string, DisplayRow, null>>(write => {
          if (write.removed && !state.index.has(write.ref) && !state.shown.some(entry => entry.kind === 'row' && entry.ref === write.ref)) return [{ t: 'remove' as const, key: write.ref }]
          const row = write.row ?? rows.item(write.ref).peek()?.row
          return row ? [{ t: 'upsert' as const, value: { row, removed: write.removed ?? false } }] : []
        })
        rows.apply(ops)
        if (next.committed) rows.apply(before.shown.flatMap(entry => entry.kind === 'row' && !state.index.has(entry.ref) ? [{ t: 'remove' as const, key: entry.ref }] : []))
        structure.value = state.shown
        if (state.target !== before.target || state.shown !== before.shown) pending.value = pendingCount(state)
        deadline.value = state.guardUntil
        partitions.value = state.partitions
      })
      return next.committed
    },
    /** Keyboard movement follows the structure the person can see, including while its next order is held. */
    navigate(ref: string | undefined, move: -1 | 1 | 'first' | 'last') {
      let first: string | undefined, last: string | undefined, before: string | undefined, after: string | undefined, found = false
      for (const entry of state.shown) {
        if (entry.kind !== 'row' || !state.index.has(entry.ref)) continue
        first ??= entry.ref
        if (entry.ref === ref) { before = last; found = true }
        else if (found && after === undefined) after = entry.ref
        last = entry.ref
      }
      return move === 'first' ? first : move === 'last' ? last : !found ? first : move === 1 ? after ?? ref : before ?? ref
    },
    canOpen(ref: string, now = Date.now()) { return state.index.has(ref) && now >= (state.moved.get(ref) ?? 0) },
    dispose() { rows.dispose() },
  }
}
export type SessionsDisplay = ReturnType<typeof createDisplay>
